import { useEffect, useRef, useState, useCallback } from 'react';
import {
  MultiFormatReader,
  BinaryBitmap,
  HybridBinarizer,
  RGBLuminanceSource,
  DecodeHintType,
  BarcodeFormat,
} from '@zxing/library';
import { T } from '../design-system';

// ── Live camera barcode scanner ──────────────────────────────────────────────
// Reads standard black-on-white labels AND inverted white-on-black labels.
// The trick: each frame is decoded twice — once with the raw luminance, and if
// that finds nothing, once with an inverted copy (v → 255 - v). Every 1-D/2-D
// decoder assumes dark bars on a light field, so the inverted pass is what makes
// a negative (white-line-on-black) label readable without a second camera or a
// user "mode" toggle.
//
// Props:
//   onDetected(text, format)  — fired per successful read (deduped by cooldown)
//   onClose()                 — user dismissed the scanner
//   title, subtitle           — optional header copy
//   cooldownMs                — ignore a repeat of the SAME code within this window

const FORMATS = [
  BarcodeFormat.CODE_128,   // what our jsbarcode labels print
  BarcodeFormat.CODE_39,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.ITF,
  BarcodeFormat.QR_CODE,
];

// Downscale wide frames before decoding — a barcode is legible at ~640px and the
// per-pixel luminance loop runs twice, so smaller frames keep the loop at video rate.
const MAX_W = 640;
const DECODE_EVERY_MS = 120;

function buildReader() {
  const reader = new MultiFormatReader();
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, FORMATS);
  hints.set(DecodeHintType.TRY_HARDER, true);
  reader.setHints(hints);
  return reader;
}

// Draw the video frame to a canvas and return its grayscale luminance buffer.
function frameToLuminance(video, canvas) {
  const vw = video.videoWidth, vh = video.videoHeight;
  if (!vw || !vh) return null;
  const scale = Math.min(1, MAX_W / vw);
  const w = Math.round(vw * scale), h = Math.round(vh * scale);
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(video, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h); // RGBA
  const lum = new Uint8ClampedArray(w * h);
  for (let i = 0, j = 0; j < lum.length; i += 4, j++) {
    // green-favouring luminance, matches ZXing's own weighting
    lum[j] = (data[i] * 306 + data[i + 1] * 601 + data[i + 2] * 117) >> 10;
  }
  return { lum, w, h };
}

function decodeLuminance(reader, lum, w, h) {
  try {
    const src = new RGBLuminanceSource(lum, w, h);
    const bitmap = new BinaryBitmap(new HybridBinarizer(src));
    const res = reader.decode(bitmap);
    return res ? { text: res.getText(), format: res.getBarcodeFormat() } : null;
  } catch {
    return null; // NotFoundException / ChecksumException / FormatException
  } finally {
    reader.reset();
  }
}

export default function BarcodeScanner({
  onDetected,
  onClose,
  title = 'Scan barcode',
  subtitle = 'Point at the barcode — works on white-on-black labels too',
  cooldownMs = 1500,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const readerRef = useRef(null);
  const rafRef = useRef(0);
  const lastDecodeTs = useRef(0);
  const lastHit = useRef({ text: '', ts: 0 });
  const streamRef = useRef(null);
  const trackRef = useRef(null);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [flash, setFlash] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);

  const stop = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  const tick = useCallback((now) => {
    rafRef.current = requestAnimationFrame(tick);
    const video = videoRef.current, canvas = canvasRef.current, reader = readerRef.current;
    if (!video || !canvas || !reader || video.readyState < 2) return;
    if (now - lastDecodeTs.current < DECODE_EVERY_MS) return;
    lastDecodeTs.current = now;

    const frame = frameToLuminance(video, canvas);
    if (!frame) return;
    const { lum, w, h } = frame;

    // Pass 1: as-is (black-on-white). Pass 2: inverted (white-on-black).
    let hit = decodeLuminance(reader, lum, w, h);
    if (!hit) {
      const inv = new Uint8ClampedArray(lum.length);
      for (let i = 0; i < lum.length; i++) inv[i] = 255 - lum[i];
      hit = decodeLuminance(reader, inv, w, h);
    }
    if (!hit) return;

    // Dedupe: same code within the cooldown is one physical item, not a new scan.
    const t = performance.now();
    if (hit.text === lastHit.current.text && t - lastHit.current.ts < cooldownMs) return;
    lastHit.current = { text: hit.text, ts: t };

    setFlash(true);
    setTimeout(() => setFlash(false), 180);
    try { navigator.vibrate?.(60); } catch { /* no haptics */ }
    onDetected?.(hit.text, hit.format);
  }, [onDetected, cooldownMs]);

  useEffect(() => {
    let cancelled = false;
    readerRef.current = buildReader();
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        trackRef.current = track;
        const caps = track.getCapabilities?.() || {};
        setTorchSupported(!!caps.torch);
        const video = videoRef.current;
        video.srcObject = stream;
        video.setAttribute('playsinline', 'true');
        await video.play();
        setReady(true);
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        setError(
          e?.name === 'NotAllowedError'
            ? 'Camera permission denied. Enable camera access and reopen.'
            : e?.name === 'NotFoundError'
              ? 'No camera found on this device.'
              : `Could not start camera: ${e?.message || e}`,
        );
      }
    })();
    return () => { cancelled = true; stop(); };
  }, [tick, stop]);

  const toggleTorch = async () => {
    const track = trackRef.current;
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(v => !v);
    } catch { /* torch not controllable */ }
  };

  const close = () => { stop(); onClose?.(); };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.92)',
      display: 'flex', flexDirection: 'column', fontFamily: T.font,
    }}>
      <div style={{ padding: '16px 20px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: T.type.md, fontWeight: 700 }}>{title}</div>
          <div style={{ fontSize: T.type.xs, opacity: 0.7 }}>{subtitle}</div>
        </div>
        <button onClick={close} aria-label="Close scanner" style={{
          background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff',
          width: 36, height: 36, borderRadius: 18, fontSize: T.type.lg, cursor: 'pointer',
        }}>✕</button>
      </div>

      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <video ref={videoRef} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* scan reticle */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: 'min(78vw, 460px)', height: 160, borderRadius: 14,
          border: `3px solid ${flash ? T.success : 'rgba(255,255,255,0.85)'}`,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
          transition: 'border-color 0.15s',
        }} />

        {!ready && !error && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: T.type.base }}>
            Starting camera…
          </div>
        )}
        {error && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ background: '#fff', color: T.danger, borderRadius: T.r.lg, padding: 20, maxWidth: 360, textAlign: 'center', fontSize: T.type.base }}>
              {error}
              <button onClick={close} style={{ display: 'block', margin: '14px auto 0', padding: '8px 18px', background: T.danger, color: '#fff', border: 'none', borderRadius: T.r.md, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        {ready && !error && !torchSupported && (
          // iOS Safari (and some browsers) can't control the flash — say so instead
          // of silently hiding the torch, so users don't hunt for a missing button.
          <div style={{ fontSize: T.type.xs, color: 'rgba(255,255,255,0.6)' }}>
            Torch not available on this device — use good lighting
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          {torchSupported && (
            <button onClick={toggleTorch} style={{
              padding: '10px 18px', borderRadius: T.r.md, border: 'none', cursor: 'pointer',
              background: torchOn ? '#facc15' : 'rgba(255,255,255,0.15)', color: torchOn ? '#111' : '#fff', fontWeight: 600,
            }}>{torchOn ? '🔦 Torch on' : '🔦 Torch'}</button>
          )}
          <button onClick={close} style={{
            padding: '10px 28px', borderRadius: T.r.md, border: 'none', cursor: 'pointer',
            background: '#fff', color: '#111', fontWeight: 700,
          }}>Done</button>
        </div>
      </div>
    </div>
  );
}
